import { Flex, Heading, Stack } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { StyledScrollWrapper } from '$/components/common/StyledScrollWrapper';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { MaterialDetailsModal } from '$/components/core/Collection/MaterialDetailsModal';
import { EmptyStateCard } from '$/components/core/EmptyStateCard';
import { useIsMobile } from '$/hooks/useIsMobile';
import { BackToTopButton } from '$/pages/DashboardPages/components/BackToTopButton';
import { FavoriteDashboardContent } from '$/pages/DashboardPages/pages/Favorite/components/FavoriteDashboardContent';
import { FavoriteFolderView } from '$/pages/DashboardPages/pages/Favorite/components/FavoriteFolderView';
import { FavoriteModal } from '$/pages/DashboardPages/pages/Favorite/components/FavoriteModal';
import { type Material } from '$/services/usecases/materials/mapper/materials';
import { useFavoriteStore } from '$/stores/useFavoriteStore';

export type FavoriteItem = {
  material: Material;
  parentFolders: string[];
  isGlobalFavoriteMaterial: boolean;
};

export const FavoriteDashboard = () => {
  const { t } = useTranslation();
  const activeFolderId = useFavoriteStore.useActiveFolderId();
  const favorites = useFavoriteStore.useFavorites();
  const isMobile = useIsMobile();
  const user = useAuthenticationStore.useUser();

  useEffect(() => {
    if (user != null && favorites.length === 0) {
      void useFavoriteStore.getState().getFavorites(true);
    }
  }, [favorites.length, user]);

  const ref = useRef<HTMLDivElement>(null);

  if (favorites.length === 0) {
    return (
      <Stack
        flex='1'
        gap='0'
        w='full'
        minH='0'
        {...(!isMobile && {
          layerStyle: 'DashboardSection',
          p: '7',
        })}
      >
        <Heading as='h1' flexShrink={0} mb='8' fontSize='3xl'>
          {t('favorites.pageHeader')}
        </Heading>
        <Flex align='center' justify='center' flex='1' minH='0'>
          <EmptyStateCard
            title={t('favorites.empty.title')}
            description={t('favorites.empty.description')}
            buttonText={t('favorites.empty.discoverCollections')}
            to='/collections'
          />
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack
      ref={ref}
      w='full'
      {...(!isMobile && {
        layerStyle: 'DashboardSection',
      })}
    >
      <StyledScrollWrapper>
        <Stack
          gap='0'
          overflowY='auto'
          w='full'
          {...(!isMobile && {
            p: '7',
          })}
        >
          {activeFolderId == null ? (
            <FavoriteDashboardContent sectionRef={ref} />
          ) : (
            <FavoriteFolderView />
          )}
          <MaterialDetailsModal />
          <FavoriteModal />

          <BackToTopButton containerRef={ref} right='65px' bottom='65px' />
        </Stack>
      </StyledScrollWrapper>
    </Stack>
  );
};
